// src/components/tutor/TutorFab.tsx
//
// Floating action button + slide-out panel that lets the student chat with
// the tutor from any authenticated page. Provides page context (path,
// surface, questionId if discoverable from the URL) on each send so the
// tutor knows where the student is.
//
// PERSONA + PROACTIVE NUDGES (phases 1–2 of the tutor-liveliness work): the
// tutor is named "Sage" and introduces itself with a once-per-session "peek"
// teaser + eye-drawing pulse (phase 1), AND reacts to events via the nudge bus
// (phase 2) — the practice flow emits a nudge on a wrong answer / hot streak,
// which Sage surfaces as a priority peek that opens the chat pre-filled with a
// ready-to-send prompt + the question's context. (Phase 3 adds a daily
// challenge.)
//
// Hidden on:
//   - Sign-in / sign-up pages
//   - The /tutor page itself (the dedicated chat is right there)
//   - Test/SAT in-progress pages (don't distract students mid-test)
//   - Unauthenticated pages
//
// Interaction model: the panel renders WITHOUT a modal backdrop (it's a
// sidebar, not a dialog) so the rest of the page stays interactive. Click
// outside or Escape closes it — UNLESS it's docked (roadmap 0.3): the student
// can pin Sage to the side (lg+ only) so it stays open while they work, with the
// page content padded to its left. The dock preference persists across reloads.

'use client'

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import Link from 'next/link'
import { Sparkles, X, PanelRightClose, PanelRightOpen } from 'lucide-react'
import TutorChatPanel from './TutorChatPanel'
import { onTutorNudge, onTutorNudgeClear } from '@/lib/tutor/nudge-bus'
import { getActiveQuestion, onActiveQuestionChange } from '@/lib/tutor/active-question'
import { markSageOpened } from '@/lib/tutor/sage-open-signal'
import {
  getOnScreenMaterial,
  onAskSage,
  onOnScreenMaterialChange,
  type OnScreenMaterial,
  type SageDrillRef,
} from '@/lib/tutor/on-screen-material'
import { useSchoolAiBlocked } from '@/components/school/SchoolAiAccess'

const TUTOR_NAME = 'Sage'
const PEEK_SESSION_KEY = 'tutor-peek-shown'
const PEEK_DELAY_MS = 2500
// Persists the "dock Sage to the side" preference (roadmap 0.3 — docked tutor).
// Survives full reloads; client-side navigations keep the live state since the
// FAB lives in the root layout and isn't remounted.
const DOCK_KEY = 'sage-docked'

const HIDDEN_PATH_PREFIXES = [
  '/signin',
  '/signup',
  '/tutor', // dedicated page already has the chat
  '/sat', // mid-test
  '/test/', // mid-test
  // A mistake-notebook check is evidence the student can do it unaided
  // (src/lib/notebook/recovery.ts) — no tutor while it's on screen.
  '/dashboard/notebook/check',
]

const SURFACE_LABELS: Array<{ prefix: string; label: string }> = [
  { prefix: '/dashboard', label: 'Dashboard' },
  { prefix: '/study-plan', label: 'Study plan' },
  { prefix: '/practice/adaptive', label: 'Adaptive practice' },
  { prefix: '/practice/session', label: 'Practice session' },
  { prefix: '/practice/drill', label: 'Drill' },
  { prefix: '/practice', label: 'Practice' },
  { prefix: '/learn', label: 'Learn' },
  { prefix: '/videos', label: 'Videos' },
  { prefix: '/review', label: 'Review' },
  { prefix: '/attempt', label: 'Attempt review' },
  { prefix: '/admin', label: 'Admin' },
]

function deriveSurface(path: string | null): string | null {
  if (!path) return null
  for (const { prefix, label } of SURFACE_LABELS) {
    if (path.startsWith(prefix)) return label
  }
  return null
}

/**
 * Best-effort questionId extraction from the URL. Many practice pages
 * carry the question id as a query param or the last path segment of a
 * /question/<id> sub-route. The server validates the id anyway.
 */
function deriveQuestionId(path: string | null, search: string | null): string | null {
  if (!path) return null
  const params = new URLSearchParams(search ?? '')
  const q = params.get('questionId') ?? params.get('q')
  if (q && q.length > 0 && q.length < 64) return q
  const m = path.match(/\/question\/([a-zA-Z0-9]+)/)
  if (m && m[1]) return m[1]
  return null
}

/**
 * Surface-aware teaser copy so Sage introduces itself with something useful
 * rather than a generic "hi". Phase 1 is static-per-surface; Phase 2 swaps in
 * event-driven nudges (wrong answer / weak topic / streak).
 */
function peekMessage(surface: string | null): string {
  switch (surface) {
    case 'Practice session':
    case 'Adaptive practice':
    case 'Drill':
      return "Stuck on one? Tap me for a hint — I won't give it away."
    case 'Dashboard':
      return "Not sure what to study? Ask me what'll move your score most."
    case 'Learn':
      return 'Confused by a lesson? I can explain it a different way.'
    case 'Review':
      return 'Forgetting a card? I can help make it stick.'
    default:
      return `Hi, I'm ${TUTOR_NAME} 👋 Tap me whenever you're stuck.`
  }
}

// Sage (the AI tutor) is a PREMIUM feature. The FAB shows to every signed-in
// user (the "view"), but a non-premium user who opens it gets an upgrade prompt
// instead of the chat input (the server also enforces this with a 403).
//
// `isPremium` is resolved server-side in the root layout from the COMP-AWARE
// effective plan (getCurrentEffectivePlan), NOT from session.user.plan — that
// raw Stripe field is comp-blind and would wrongly upsell a comp-granted
// Premium tester. We keep useSession only for the auth `status`.
// `isPaid` (comp-aware, PRO) drives the free-tier 48h-retention notice inside
// the panel; it defaults to true so older mounts never show a wrong notice.
export default function TutorFab(props: { isPremium: boolean; isPaid?: boolean }) {
  // A school that has turned the AI tutor off gets no button at all.
  if (useSchoolAiBlocked()) return null
  return <TutorFabInner {...props} />
}

function TutorFabInner({ isPremium, isPaid = true }: { isPremium: boolean; isPaid?: boolean }) {
  const { status } = useSession()
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  // Docked mode (roadmap 0.3): Sage pins to the right as a persistent side panel
  // that stays open while the student works, instead of the floating overlay that
  // closes on click-outside. Restored from localStorage on full reloads (lg+ only).
  const [docked, setDocked] = useState(false)
  // Unified peek: the generic once-per-session intro (Phase 1) AND event-driven
  // nudges (Phase 2) both render through this. A nudge carries an optional
  // pre-fill prompt + the question it's about.
  const [peek, setPeek] = useState<{
    message: string
    prefill?: string
    questionId?: string
    scrollToId?: string
  } | null>(null)
  // Context carried into the chat when opened FROM a peek/nudge — a pre-filled
  // prompt + the question's id so the tutor opens with full context. Cleared on
  // a plain FAB open.
  // An "Ask Sage about this example" open carries the item it's about and a
  // draft for the composer (on-screen-material.ts); `nonce` remounts the panel
  // so a second ask while Sage is already open still seeds its draft.
  const [openCtx, setOpenCtx] = useState<{
    initialInput?: string
    questionId?: string
    material?: OnScreenMaterial
    draft?: string
    nonce?: number
    /** Sent with the auto-sent first message only ("What does this mean?" on a drill). */
    sendContext?: { drill: SageDrillRef }
  }>({})
  const panelRef = useRef<HTMLDivElement | null>(null)
  const openRef = useRef(false)

  const isHidden = HIDDEN_PATH_PREFIXES.some((p) => pathname?.startsWith(p))

  // Bumped when the welcome tour closes, so the peek effect below re-arms and
  // Sage introduces itself AFTER the tour rather than pinging behind its dim.
  const [tourClosedTick, setTourClosedTick] = useState(0)

  useEffect(() => {
    openRef.current = open
    // "Fix this mistake" counts a fresh question solved with Sage open as
    // "with help" (src/lib/tutor/sage-open-signal.ts).
    if (open) markSageOpened()
  }, [open])

  // Restore the docked preference on a full reload — only on wide viewports,
  // where there's room for a side-by-side panel. (Client-side navigations keep
  // the live state without this, since the layout-level FAB isn't remounted.)
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      if (
        localStorage.getItem(DOCK_KEY) === '1' &&
        window.matchMedia('(min-width: 1024px)').matches
      ) {
        setDocked(true)
        setOpen(true)
      }
    } catch {
      // localStorage unavailable — start undocked
    }
  }, [])

  // Reserve space for the docked panel by padding the document (globals.css pushes
  // header + main left of the panel on lg+). Only while it's actually showing —
  // never leave the page padded with no panel (e.g. on a hidden route).
  useEffect(() => {
    const el = document.documentElement
    if (open && docked && !isHidden) el.dataset.sageDocked = '1'
    else delete el.dataset.sageDocked
    return () => {
      delete el.dataset.sageDocked
    }
  }, [open, docked, isHidden])

  useEffect(() => {
    const onTourClosed = () => setTourClosedTick((t) => t + 1)
    window.addEventListener('ut:welcome-tour-closed', onTourClosed)
    return () => window.removeEventListener('ut:welcome-tour-closed', onTourClosed)
  }, [])

  // Proactive nudges (Phase 2): the practice flow emits a nudge on a wrong
  // answer / hot streak; Sage surfaces it as a priority peek — unless the panel
  // is already open (the student's already with us).
  useEffect(() => {
    return onTutorNudge((n) => {
      if (openRef.current) return
      setPeek({
        message: n.message,
        prefill: n.prefill,
        questionId: n.questionId,
        scrollToId: n.scrollToId,
      })
    })
  }, [])

  // A nudge's moment has passed (e.g. the student moved to another practice
  // question) — drop a stale NUDGE peek so it can't be tapped on the wrong
  // question with a now-wrong prefill. Only nudges are cleared (they carry a
  // prefill / questionId / scrollToId); the once-per-session intro peek, which
  // has only a message, is left alone.
  useEffect(() => {
    return onTutorNudgeClear(() =>
      setPeek((prev) =>
        prev && (prev.prefill || prev.questionId || prev.scrollToId) ? null : prev,
      ),
    )
  }, [])

  // The question on screen, for surfaces that keep it in React state instead of
  // the URL (practice sessions). Server snapshot is null: there is no active
  // question during SSR, and returning anything else would hydrate-mismatch.
  const activeQuestionId = useSyncExternalStore(
    onActiveQuestionChange,
    getActiveQuestion,
    () => null,
  )

  // The lesson / video on screen, published by those pages.
  const material = useSyncExternalStore(onOnScreenMaterialChange, getOnScreenMaterial, () => null)

  const pageContext = useMemo(() => {
    if (typeof window === 'undefined') {
      return { path: pathname ?? undefined, surface: deriveSurface(pathname) ?? undefined }
    }
    const search = window.location.search
    return {
      path: pathname ?? undefined,
      surface: deriveSurface(pathname) ?? undefined,
      // URL first (it is the more explicit signal), then the published active
      // question. Without the fallback, opening Sage from the FAB during a
      // practice session attaches NO question and the panel shows only the old
      // conversation — the 2026-07-24 report.
      questionId: deriveQuestionId(pathname, search) ?? activeQuestionId ?? undefined,
      // Ids only; the server loads the examples / walkthrough itself.
      lesson: material?.lesson,
      video: material?.video,
    }
  }, [pathname, activeQuestionId, material])

  // What Sage is actually anchored to: an explicit open (nudge / "Ask Tutor",
  // which passes the id) wins over whatever the page is showing.
  const effectiveContext = useMemo(() => {
    const asked = openCtx.material
    return {
      ...pageContext,
      ...(openCtx.questionId ? { questionId: openCtx.questionId } : {}),
      ...(asked?.lesson ? { lesson: asked.lesson } : {}),
      ...(asked?.video ? { video: asked.video } : {}),
    }
  }, [openCtx.questionId, openCtx.material, pageContext])
  // What the header says Sage can see: the item asked about, else the page's.
  const materialLabel = openCtx.material?.label ?? material?.label ?? null

  // Peek teaser: once per session, a short delay after landing, only while the
  // panel is closed and the FAB is actually visible. We mark it "shown" as soon
  // as it appears so it's one gentle introduction per session, not per page.
  useEffect(() => {
    if (status !== 'authenticated' || open || isHidden) return
    if (typeof window === 'undefined') return
    // Don't arm the intro while the welcome tour is open — it would ping behind
    // the overlay's dim. The tour dispatches 'ut:welcome-tour-closed' on close,
    // which bumps tourClosedTick and re-runs this effect to arm it then.
    if (document.documentElement.dataset.welcomeTourActive === '1') return
    try {
      if (sessionStorage.getItem(PEEK_SESSION_KEY) === '1') return
    } catch {
      return
    }
    const t = setTimeout(() => {
      // Don't override a live nudge with the generic intro.
      setPeek((prev) => prev ?? { message: peekMessage(deriveSurface(pathname) ?? null) })
      try {
        sessionStorage.setItem(PEEK_SESSION_KEY, '1')
      } catch {
        // sessionStorage unavailable (private mode quota) — the peek still
        // shows this once; we just can't remember it across navigations.
      }
    }, PEEK_DELAY_MS)
    return () => clearTimeout(t)
  }, [status, open, isHidden, pathname, tourClosedTick])

  function dismissPeek() {
    setPeek(null)
  }

  function openTutor(ctx?: { prefill?: string; questionId?: string }) {
    setOpen(true)
    setPeek(null)
    setOpenCtx({ initialInput: ctx?.prefill, questionId: ctx?.questionId })
  }

  // "Ask Sage about this example / problem / video" buttons. Listening only
  // while Sage can open here, so those buttons hide for a signed-out visitor.
  useEffect(() => {
    if (status !== 'authenticated' || isHidden) return
    return onAskSage((req) => {
      setOpen(true)
      setPeek(null)
      setOpenCtx((prev) => ({
        material: req.material,
        draft: req.draft,
        questionId: req.questionId,
        // A button that says it uses a Sage message sends on open; the drill
        // it is about rides with that one message, not every later one.
        initialInput: req.send,
        sendContext: req.drill ? { drill: req.drill } : undefined,
        nonce: (prev.nonce ?? 0) + 1,
      }))
    })
  }, [status, isHidden])

  // Pin/unpin Sage to the side. Persists the preference; pinning also opens it.
  function toggleDock() {
    setDocked((d) => {
      const next = !d
      try {
        localStorage.setItem(DOCK_KEY, next ? '1' : '0')
      } catch {
        // ignore — we just can't remember it across reloads
      }
      if (next) setOpen(true)
      return next
    })
  }

  // The X fully closes Sage — and clears the dock so it doesn't auto-reopen.
  function closePanel() {
    setOpen(false)
    setDocked(false)
    try {
      localStorage.setItem(DOCK_KEY, '0')
    } catch {
      // ignore
    }
  }

  // Click-outside + Escape close the panel — but NOT when docked (it's a
  // persistent region, not a transient overlay). Mounted only while open.
  useEffect(() => {
    if (!open || docked) return
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node | null
      if (!panelRef.current || !target) return
      if (panelRef.current.contains(target)) return
      setOpen(false)
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, docked])

  if (status !== 'authenticated') return null
  if (isHidden) return null

  return (
    <>
      {!open && (
        // max-w-[calc(100vw-2rem)] pins the whole cluster inside the viewport so
        // the peek can never hang off the right edge on a narrow phone; tighter
        // insets on mobile, roomier on sm+.
        <div className="fixed bottom-4 right-4 z-30 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2 sm:bottom-6 sm:right-6">
          {/* Peek teaser — Sage introduces itself / reacts to what just happened.
              Dismissible; tapping the message opens the chat (with the nudge's
              pre-filled prompt + question context when it came from one). */}
          {peek && (
            <div
              role="status"
              className="relative max-w-[15rem] rounded-2xl rounded-br-sm border border-border bg-card px-3 py-2 text-sm text-foreground shadow-lg"
            >
              <button
                type="button"
                onClick={dismissPeek}
                aria-label="Dismiss tutor tip"
                className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow transition hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => {
                  // A nudge can point at an on-page element (e.g. the daily
                  // challenge) instead of opening the chat.
                  if (peek.scrollToId) {
                    document
                      .getElementById(peek.scrollToId)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    setPeek(null)
                  } else {
                    openTutor(peek)
                  }
                }}
                className="block text-left"
              >
                <span className="font-semibold text-brand-ink">{TUTOR_NAME}:</span> {peek.message}
                {/* Tapping a prefilled tip SENDS it — that is deliberate (the
                    peek promises a hint, and seeding the box without sending
                    doesn't keep the promise). But the cost has to be visible
                    BEFORE the tap, not discovered after: a student reported
                    losing one of three daily messages without typing
                    anything. A tip that only scrolls somewhere costs nothing
                    and says nothing. */}
                {!peek.scrollToId && peek.prefill && (
                  <span className="mt-1 block text-xs text-muted-foreground">
                    Uses 1 of your daily {TUTOR_NAME} messages
                  </span>
                )}
              </button>
            </div>
          )}

          {/* The FAB — a brand-gradient avatar for Sage, livelier than the old
              flat circle. Pulses + shows an unread dot while a tip is waiting. */}
          <button
            type="button"
            data-tour="sage"
            onClick={() => openTutor()}
            className="relative flex h-14 w-14 items-center justify-center rounded-full bg-brand text-on-brand shadow-lg ring-2 ring-brand/30 transition-transform hover:scale-105 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand"
            aria-label={`Open ${TUTOR_NAME}, your AI tutor`}
          >
            {peek && (
              <span
                className="absolute inset-0 -z-10 animate-ping rounded-full bg-brand/40 motion-reduce:hidden"
                aria-hidden="true"
              />
            )}
            <Sparkles className="h-6 w-6" />
            {peek && (
              <span
                className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-background bg-rose-500"
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      )}

      {open && (
        <div
          ref={panelRef}
          className={`fixed bottom-0 right-0 top-0 z-40 flex w-full max-w-md flex-col border-l border-border bg-card ${
            docked ? 'shadow-xl lg:w-96 lg:max-w-none' : 'shadow-2xl sm:rounded-l-xl'
          }`}
          role={docked ? 'region' : 'dialog'}
          aria-label={`${TUTOR_NAME}, your AI tutor`}
        >
          <div className="flex items-center justify-between border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
            <div className="flex items-center gap-2">
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-on-brand"
                aria-hidden="true"
              >
                <Sparkles className="h-4 w-4" />
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground">{TUTOR_NAME}</p>
                {/* Say what Sage is answering about. TutorChatPanel has its own
                    version of this line, but only in the FULL variant — the FAB
                    renders the panel `compact` and draws this header itself, so
                    until now the anchor never appeared on the surface students
                    actually use. That is why the 2026-07-24 report ("Sage showed
                    an old conversation, nothing about this question") survived
                    the first fix. */}
                {effectiveContext.questionId ? (
                  <p className="text-xs text-brand-ink">Answering about the question on screen</p>
                ) : materialLabel ? (
                  <p className="text-xs text-brand-ink" data-testid="sage-material-label">
                    {openCtx.material ? `About ${materialLabel}` : `Can see: ${materialLabel}`}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {pageContext.surface
                      ? `${pageContext.surface} · your AI tutor`
                      : 'Your AI tutor'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Dock toggle — pin Sage to the side so it stays open while you
                  work. Only on lg+, where there's room for a side-by-side panel. */}
              <button
                type="button"
                onClick={toggleDock}
                aria-label={docked ? 'Unpin tutor' : 'Pin tutor to the side'}
                aria-pressed={docked}
                title={docked ? 'Unpin' : 'Dock to side'}
                className="hidden h-8 w-8 items-center justify-center rounded-full bg-muted text-foreground transition hover:bg-muted-foreground/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand lg:flex"
              >
                {docked ? (
                  <PanelRightOpen className="h-4 w-4" />
                ) : (
                  <PanelRightClose className="h-4 w-4" />
                )}
              </button>
              <button
                type="button"
                onClick={closePanel}
                aria-label="Close tutor"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-foreground transition hover:bg-muted-foreground/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            {isPremium ? (
              <TutorChatPanel
                key={openCtx.nonce ?? 0}
                variant="compact"
                isPaid={isPaid}
                pageContext={effectiveContext}
                initialInput={openCtx.initialInput}
                initialInputContext={openCtx.sendContext}
                initialDraft={openCtx.draft}
                // Opening an activity from a Start card closes the floating
                // panel — it's full-width on a phone, so it would otherwise sit
                // on top of the session the student just started. A docked
                // panel stays: it's beside the page, and the student pinned it
                // there to keep Sage open while they work.
                onLaunch={() => {
                  if (!docked) setOpen(false)
                }}
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                <span
                  className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand text-on-brand"
                  aria-hidden="true"
                >
                  <Sparkles className="h-6 w-6" />
                </span>
                <p className="text-base font-semibold text-foreground">
                  {TUTOR_NAME} is a Pro feature
                </p>
                <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-muted-foreground">
                  Chat with your AI tutor about any question, get step-by-step help, and have it
                  reshape your study plan. Upgrade to Pro to unlock it.
                </p>
                <Link
                  href="/pricing"
                  className="mt-5 inline-flex h-11 items-center justify-center rounded-md bg-brand px-6 text-sm font-semibold text-on-brand transition hover:bg-brand-hover"
                >
                  Upgrade to Pro
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
